// ===========================================
// Color Picker Field Component
// ===========================================
// Reusable color picker with brand presets and custom option
// Used in admin forms for instruments, lesson types, and teachers
// Supports fetching colors from school palette settings

import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  FormHelperText,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
} from '@mui/material';
import { Check as CheckIcon, Clear as ClearIcon } from '@mui/icons-material';
import { colorPickerPresets } from '../../styles/brandColors';
import { useColorPalette } from '../../hooks/useAdminSettings';

// ===========================================
// TYPES
// ===========================================

interface ColorPreset {
  value: string;
  label: string;
  textColor: string;
}

export interface ColorPickerFieldProps {
  label?: string;
  value: string | null;
  onChange: (color: string | null) => void;
  helperText?: string;
  error?: boolean;
  required?: boolean;
  showClear?: boolean;
  /**
   * When true, fetches colors from school settings palette.
   * Falls back to brand colors if school palette is empty or loading.
   */
  useSchoolPalette?: boolean;
  /** Read-only display: every swatch and the clear button are inert. Default false. */
  disabled?: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function ColorPickerField({
  label = 'Color',
  value,
  onChange,
  helperText,
  error = false,
  required = false,
  showClear = true,
  useSchoolPalette = false,
  disabled = false,
}: ColorPickerFieldProps) {
  const [showCustom, setShowCustom] = useState(false);
  const [customColor, setCustomColor] = useState(value || '#4580E4');

  // Fetch school palette if requested
  const { data: schoolPalette, isLoading: isPaletteLoading } = useColorPalette({
    enabled: useSchoolPalette,
  });

  // Determine which presets to use
  const presets: ColorPreset[] = useMemo(() => {
    if (useSchoolPalette && schoolPalette) {
      // Combine school colors with brand defaults if enabled
      const schoolColors = schoolPalette.colors.map((c) => ({
        value: c.value,
        label: c.label,
        textColor: c.textColor,
      }));

      if (schoolPalette.useDefaults) {
        // Merge brand colors, avoiding duplicates
        const brandValues = new Set(schoolColors.map((c) => c.value.toUpperCase()));
        const uniqueBrandColors = colorPickerPresets.filter(
          (c) => !brandValues.has(c.value.toUpperCase())
        );
        return [...schoolColors, ...uniqueBrandColors];
      }

      return schoolColors.length > 0 ? schoolColors : [...colorPickerPresets];
    }

    // Default to brand presets
    return [...colorPickerPresets];
  }, [useSchoolPalette, schoolPalette]);

  // Check if current value is a preset
  const isPreset = presets.some((p) => p.value.toUpperCase() === value?.toUpperCase());

  // If value is not a preset and not null, show custom picker
  useEffect(() => {
    if (value && !isPreset) {
      setShowCustom(true);
      setCustomColor(value);
    }
  }, [value, isPreset]);

  const handlePresetClick = (presetValue: string) => {
    setShowCustom(false);
    onChange(presetValue);
  };

  const handleCustomClick = () => {
    setShowCustom(true);
    onChange(customColor);
  };

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setCustomColor(newColor);
    onChange(newColor);
  };

  const handleClear = () => {
    setShowCustom(false);
    onChange(null);
  };

  // Show loading state when fetching school palette
  if (useSchoolPalette && isPaletteLoading) {
    return (
      <FormControl fullWidth error={error}>
        {label && (
          <FormLabel required={required} sx={{ mb: 1, fontSize: '0.875rem' }}>
            {label}
          </FormLabel>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="caption" color="text.secondary">
            Loading colors...
          </Typography>
        </Box>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth error={error}>
      {label && (
        <FormLabel required={required} sx={{ mb: 1, fontSize: '0.875rem' }}>
          {label}
        </FormLabel>
      )}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {/* Color Presets */}
        {presets.map((preset) => (
          <Tooltip key={preset.value} title={disabled ? '' : preset.label}>
            <IconButton
              disabled={disabled}
              onClick={() => handlePresetClick(preset.value)}
              sx={{
                width: 36,
                height: 36,
                bgcolor: preset.value,
                border: '2px solid',
                borderColor: value?.toUpperCase() === preset.value.toUpperCase() ? 'primary.main' : 'divider',
                borderRadius: 1,
                '&:hover': {
                  bgcolor: preset.value,
                  opacity: 0.9,
                },
              }}
            >
              {value?.toUpperCase() === preset.value.toUpperCase() && (
                <CheckIcon
                  sx={{
                    color: preset.textColor,
                    fontSize: 18,
                  }}
                />
              )}
            </IconButton>
          </Tooltip>
        ))}

        {/* Custom Color Button/Input */}
        <Tooltip title={disabled ? '' : 'Custom Color'}>
          <Box
            sx={{
              position: 'relative',
              width: 36,
              height: 36,
            }}
          >
            {showCustom ? (
              <input
                type="color"
                value={customColor}
                onChange={handleCustomChange}
                disabled={disabled}
                style={{
                  width: 36,
                  height: 36,
                  border: `2px solid ${value === customColor && !isPreset ? '#4580E4' : '#e0e0e0'}`,
                  borderRadius: 4,
                  padding: 0,
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                }}
              />
            ) : (
              <IconButton
                aria-label="Custom Color"
                disabled={disabled}
                onClick={handleCustomClick}
                sx={{
                  width: 36,
                  height: 36,
                  background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
                  border: '2px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&:hover': {
                    opacity: 0.9,
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontSize: 14, fontWeight: 'bold', color: '#fff', textShadow: '0 0 2px #000' }}
                >
                  +
                </Typography>
              </IconButton>
            )}
          </Box>
        </Tooltip>

        {/* Clear Button */}
        {showClear && value && (
          <Tooltip title={disabled ? '' : 'Clear color'}>
            <IconButton size="small" onClick={handleClear} disabled={disabled} sx={{ ml: 0.5 }}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Color Preview */}
        {value && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ml: 1,
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                bgcolor: value,
                borderRadius: 0.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {value.toUpperCase()}
            </Typography>
          </Box>
        )}
      </Stack>

      {helperText && <FormHelperText sx={{ mt: 1 }}>{helperText}</FormHelperText>}
    </FormControl>
  );
}
